import { useForm } from '@magicdoor/form';
import { useLocalization } from '@magicdoor/ui';
import { useParams } from '@solidjs/router';
import { createSignal } from 'solid-js';
import { toast } from '~/components/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { leaseRentalInsuranceRepository } from '~/repositories/leaseRentalInsuranceRepository';
import { createLazyResource, createMutation } from '~/utils/resource';

export const [LeaseRentalInsuranceProvider, useLeaseRentalInsurance] = createMagicDoorContext('LeaseRentalInsurance', () => {
  const params = useParams<{ leaseId: string }>();
  const { t } = useLocalization();
  const form = useForm();
  const [isModalOpen, setIsModalOpen] = createSignal(false);
  const [selectedInsurance, setSelectedInsurance] = createSignal<MagicDoor.Api.LeaseRentersInsuranceDto | null>(null);

  const [insurances, { refetch: refetchInsurances }] = createLazyResource(async () => {
    return await leaseRentalInsuranceRepository.getAll(params.leaseId);
  });

  const handleAdd = () => {
    setSelectedInsurance(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (insurance: MagicDoor.Api.LeaseRentersInsuranceDto) => {
    setSelectedInsurance(insurance);
    setIsModalOpen(true);
    form.setFormStore(insurance);
  };

  const handleSubmit = createMutation(async () => {
    const leaseId = params.leaseId;
    if (!leaseId) return;

    const { validateStatus } = form.validateForm();

    if (!validateStatus) return;

    const data = form.formStore as MagicDoor.Api.CreateLeaseRentersInsuranceDto | MagicDoor.Api.UpdateLeaseRentersInsuranceDto;

    if (selectedInsurance()) {
      await leaseRentalInsuranceRepository.update(leaseId, selectedInsurance()!.id, data as MagicDoor.Api.UpdateLeaseRentersInsuranceDto);
      toast.success(t('Insurance updated successfully'));
    } else {
      await leaseRentalInsuranceRepository.create(leaseId, data as MagicDoor.Api.CreateLeaseRentersInsuranceDto);
      toast.success(t('Insurance added successfully'));
    }

    setIsModalOpen(false);
    refetchInsurances();
  });

  const handleDelete = createMutation(async (insurance: MagicDoor.Api.LeaseRentersInsuranceDto) => {
    const leaseId = params.leaseId;
    if (!leaseId) return;

    await leaseRentalInsuranceRepository.delete(leaseId, insurance.id);
    toast.success(t('Insurance deleted successfully'));
    refetchInsurances();
  });

  return {
    insurances,
    isModalOpen,
    selectedInsurance,
    form,
    handleAdd,
    handleEdit,
    handleSubmit,
    handleDelete,
    setIsModalOpen,
  };
});
